/**
 * O fluxo de triagem — a decisão, separada de tudo que faz efeito.
 *
 * ## Por que este arquivo é uma função pura
 *
 * `proximoPasso` recebe o fluxo, onde o contato está e o que ele escreveu, e devolve **o que fazer**. Não
 * grava, não envia, não olha o relógio. Tudo o que faz efeito mora em `automacao.ts`.
 *
 * A razão não é elegância: é que esta é a parte que erra caro e é impossível de testar de outra forma. Um
 * menu que não reconhece "quero marcar" como a opção 1, ou que repete a pergunta para sempre, é um cliente
 * conversando com uma parede — e isso não aparece em teste de integração, aparece em reclamação.
 *
 * ## A comparação é por CONTEÚDO, e a ordem importa
 *
 * O cliente responde "1", "opção 1", "quero marcar" ou "Marcar consulta". Exigir o texto exato faria o menu
 * falhar na maioria das respostas humanas. Então:
 *
 * 1. **número solto primeiro** — "1" casa a opção cujo `quando` é "1", e nada mais;
 * 2. **conteúdo depois** — o texto do cliente contém o `quando` da opção, ou vice-versa.
 *
 * A ordem é o que impede "1" de casar com uma opção cujo `quando` é "10 anos".
 */

export interface OpcaoDoPasso {
  /** O que o cliente escreve. Comparado por conteúdo — ver o cabeçalho. */
  readonly quando: string;
  /** Próximo passo. Ausente = termina aqui. */
  readonly vaiPara?: string | null;
  readonly filaId?: string | null;
  readonly etiquetaId?: string | null;
  /** `true` termina o fluxo nesta opção, mandando a conversa para a fila. */
  readonly encerra?: boolean;
}

export interface PassoDoFluxo {
  readonly chave: string;
  readonly pergunta: string;
  readonly opcoes: readonly OpcaoDoPasso[];
}

export interface Fluxo {
  readonly id: string;
  readonly nome: string;
  readonly quando: Gatilho;
  /** O parâmetro do gatilho, quando ele tem um. Ver `Gatilho`. */
  readonly quandoValor?: string | null;
  readonly passos: readonly PassoDoFluxo[];
  readonly passosSemResposta: number;
  readonly filaDeDesistenciaId: string | null;
}

export interface EstadoNoFluxo {
  readonly passoAtual: string | null;
  readonly repeticoes: number;
  readonly terminado: boolean;
}

/**
 * O que fazer com esta mensagem.
 *
 * `perguntar` manda o texto do passo e fica esperando. `encaminhar` termina: manda a conversa para a fila
 * (com etiqueta, se houver) e sai do caminho. `nada` é o fluxo já terminado ou desligado — e é o caso mais
 * comum depois da primeira troca.
 */
export type AcaoDoFluxo =
  | { readonly acao: "nada"; readonly porque: string }
  | { readonly acao: "perguntar"; readonly passo: string; readonly texto: string; readonly repeticoes: number }
  | {
      readonly acao: "encaminhar";
      readonly filaId: string | null;
      readonly etiquetaId: string | null;
      readonly porque: string;
      /** Texto de despedida, quando o passo tiver um. `null` = encaminha calado. */
      readonly texto: string | null;
    };

const NADA = (porque: string): AcaoDoFluxo => ({ acao: "nada", porque });

/** Normaliza para comparar: minúsculas, sem acento, sem pontuação de borda. */
function normalizar(v: string): string {
  return v
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, "")
    .trim();
}

/**
 * Qual opção o cliente escolheu.
 *
 * Exportada porque é a parte que mais erra e a que mais merece teste próprio.
 */
export function opcaoEscolhida(
  passo: PassoDoFluxo,
  respostaDoCliente: string,
): OpcaoDoPasso | null {
  const resposta = normalizar(respostaDoCliente);
  if (resposta === "") return null;

  // 1. Número solto, comparação EXATA. Vem primeiro para "1" não casar com um `quando` de "10 anos".
  if (/^\d+$/.test(resposta)) {
    const porNumero = passo.opcoes.find((o) => normalizar(o.quando) === resposta);
    if (porNumero) return porNumero;
  }

  // 2. Conteúdo, nos dois sentidos: "quero marcar consulta" casa com `quando: "marcar consulta"`, e
  //    "marcar" casa com `quando: "marcar consulta"` também.
  return (
    passo.opcoes.find((o) => {
      const quando = normalizar(o.quando);
      if (quando === "") return false;
      // `quando` de um caractere só é comparado por igualdade: um "s" (de "sim") contido em qualquer
      // frase casaria com tudo.
      if (quando.length === 1) return resposta === quando;
      return resposta.includes(quando) || quando.includes(resposta);
    }) ?? null
  );
}

export function proximoPasso(
  fluxo: Fluxo,
  estado: EstadoNoFluxo | null,
  situacao: {
    readonly textoDoCliente: string | null;
    /** Dono humano no atendimento. O fluxo **sempre** sai de cena quando alguém assumiu. */
    readonly donoId: string | null;
    readonly donoTipo: string | null;
    readonly primeiraMensagem: boolean;
    readonly dentroDoHorario: boolean;
  },
): AcaoDoFluxo {
  if (fluxo.quando === "nunca") return NADA("O fluxo está desligado.");
  if (fluxo.passos.length === 0) return NADA("O fluxo não tem passos.");

  // Alguém da equipe assumiu: o fluxo sai de cena, sempre. Um menu respondendo por cima de um atendente
  // é o cliente lendo duas vozes na mesma conversa.
  if (situacao.donoId && situacao.donoTipo !== "agente") {
    return NADA("Alguém da equipe já assumiu esta conversa.");
  }

  if (estado?.terminado) return NADA("O fluxo já terminou nesta conversa.");

  // `fora_do_horario` dentro do horário: a equipe está, e ela responde.
  if (fluxo.quando === "fora_do_horario" && situacao.dentroDoHorario) {
    return NADA("Está dentro do horário de atendimento.");
  }

  // --- Primeira mensagem: abre o menu -----------------------------------------------------------
  if (!estado || estado.passoAtual === null) {
    if (!situacao.primeiraMensagem) {
      // Conversa que já existia e nunca entrou no fluxo não entra agora: o menu apareceria no meio de
      // um assunto em andamento, que é a coisa mais irritante que este produto poderia fazer.
      return NADA("Esta conversa não começou no fluxo.");
    }
    const primeiro = fluxo.passos[0]!;
    return { acao: "perguntar", passo: primeiro.chave, texto: primeiro.pergunta, repeticoes: 0 };
  }

  const passo = fluxo.passos.find((p) => p.chave === estado.passoAtual);
  if (!passo) {
    // O passo saiu do fluxo entre uma mensagem e outra — alguém editou. Encaminhar é o certo: manter o
    // cliente num passo que não existe mais o deixaria esperando resposta que ninguém vai dar.
    return {
      acao: "encaminhar",
      filaId: fluxo.filaDeDesistenciaId,
      etiquetaId: null,
      porque: "O fluxo foi alterado enquanto esta conversa estava nele.",
      texto: null,
    };
  }

  const escolha = situacao.textoDoCliente ? opcaoEscolhida(passo, situacao.textoDoCliente) : null;

  if (!escolha) {
    const repeticoes = estado.repeticoes + 1;
    if (repeticoes > fluxo.passosSemResposta) {
      // Desistir é obrigatório. Ver a 0020: um menu que insiste indefinidamente é um cliente conversando
      // com uma parede.
      return {
        acao: "encaminhar",
        filaId: fluxo.filaDeDesistenciaId,
        etiquetaId: null,
        porque: `O contato não escolheu nenhuma opção em ${repeticoes} tentativas.`,
        texto: null,
      };
    }
    return { acao: "perguntar", passo: passo.chave, texto: passo.pergunta, repeticoes };
  }

  // Escolheu. Vai para o próximo passo, ou encerra.
  if (escolha.vaiPara) {
    const proximo = fluxo.passos.find((p) => p.chave === escolha.vaiPara);
    if (proximo) {
      return { acao: "perguntar", passo: proximo.chave, texto: proximo.pergunta, repeticoes: 0 };
    }
    // Destino inexistente: encaminha em vez de travar. Um `vaiPara` quebrado é erro de quem montou o
    // fluxo, e o cliente do cliente não pode pagar por ele ficando sem resposta.
    return {
      acao: "encaminhar",
      filaId: escolha.filaId ?? fluxo.filaDeDesistenciaId,
      etiquetaId: escolha.etiquetaId ?? null,
      porque: `A opção escolhida aponta para um passo que não existe ('${escolha.vaiPara}').`,
      texto: null,
    };
  }

  return {
    acao: "encaminhar",
    filaId: escolha.filaId ?? fluxo.filaDeDesistenciaId,
    etiquetaId: escolha.etiquetaId ?? null,
    porque: `O contato escolheu '${escolha.quando}'.`,
    texto: null,
  };
}

/**
 * Confere um fluxo antes de gravar.
 *
 * As três coisas que a tela não deve conseguir salvar, porque cada uma produz um cliente sem resposta:
 * chave repetida, passo sem opção, e `vaiPara` para um passo que não existe.
 */
export function fluxoServe(
  passos: readonly PassoDoFluxo[],
): { readonly serve: true } | { readonly serve: false; readonly motivo: string } {
  if (passos.length === 0) return { serve: false, motivo: "O fluxo precisa de pelo menos uma pergunta." };

  const chaves = new Set<string>();
  for (const p of passos) {
    const chave = p.chave?.trim();
    if (!chave) return { serve: false, motivo: "Todo passo precisa de um identificador." };
    if (chaves.has(chave)) {
      return { serve: false, motivo: `Há dois passos com o identificador '${chave}'.` };
    }
    chaves.add(chave);

    if (!p.pergunta?.trim()) {
      return { serve: false, motivo: `O passo '${chave}' não tem pergunta — o contato receberia vazio.` };
    }
    if (p.opcoes.length === 0) {
      return {
        serve: false,
        motivo:
          `O passo '${chave}' não tem nenhuma opção. Sem opção, o contato responde e o fluxo não sabe o ` +
          `que fazer — e ele repetiria a pergunta até desistir.`,
      };
    }
    for (const o of p.opcoes) {
      if (!o.quando?.trim()) {
        return { serve: false, motivo: `Uma opção do passo '${chave}' não diz o que o contato responde.` };
      }
    }
  }

  // Destinos, só depois de conhecer todas as chaves — senão um `vaiPara` para um passo mais abaixo seria
  // recusado por ainda não ter sido visto.
  for (const p of passos) {
    for (const o of p.opcoes) {
      if (o.vaiPara && !chaves.has(o.vaiPara)) {
        return {
          serve: false,
          motivo: `A opção '${o.quando}' do passo '${p.chave}' aponta para '${o.vaiPara}', que não existe.`,
        };
      }
      if (o.vaiPara === p.chave) {
        // Auto-referência é o laço mais fácil de criar sem querer, e o fluxo repetiria a mesma pergunta
        // para sempre — o `passos_sem_resposta` não protege, porque o contato ESTÁ respondendo.
        return {
          serve: false,
          motivo: `A opção '${o.quando}' do passo '${p.chave}' aponta para o próprio passo, o que faria a mesma pergunta se repetir sem fim.`,
        };
      }
    }
  }

  return { serve: true };
}

/**
 * --- A spec 12 começa aqui -----------------------------------------------------------------------
 *
 * O que vem abaixo existe para o EDITOR, e nada disto muda a execução: `proximoPasso` continua sendo a
 * única coisa que decide o que o contato recebe.
 */

/** Um problema encontrado no fluxo. `bloqueante` impede publicar; aviso não. */
export interface ProblemaDoFluxo {
  readonly bloqueante: boolean;
  /** A chave do passo, para a tela **levar até ele**. `null` = problema do fluxo inteiro. */
  readonly passo: string | null;
  readonly texto: string;
}

/**
 * Todos os problemas, não o primeiro (§3.2 da spec 12).
 *
 * ## Por que uma LISTA, e por que a contagem importa
 *
 * `fluxoServe` devolvia o primeiro problema e parava. Isso funciona para recusar um `POST`; não funciona
 * para editar: a pessoa corrige um, salva, descobre o segundo, corrige, salva, descobre o terceiro. Três
 * viagens para uma informação que estava toda disponível na primeira.
 *
 * O §3.2 pede **quantos problemas existem** e que **clicar no contador leve ao passo** — por isso cada
 * problema carrega a `chave` do passo. Contador que só conta é contador que ensina a ignorar.
 *
 * ## Bloqueante × aviso
 *
 * Bloqueante é o que produz **cliente sem resposta**: passo sem opção, destino inexistente,
 * auto-referência. Aviso é o que é feio e funciona: passo que nenhuma opção alcança, opção repetida.
 *
 * Tratar aviso como bloqueio treina a pessoa a contornar a validação — e aí o bloqueio de verdade também
 * perde autoridade.
 */
export function problemasDoFluxo(passos: readonly PassoCompleto[]): readonly ProblemaDoFluxo[] {
  const problemas: ProblemaDoFluxo[] = [];

  /*
    ## A bifurcação: menu e blocos são validados por regras diferentes

    Sem isto, publicar um fluxo de blocos era **recusado por problemas falsos**: `problemasDosBlocos` está certo,
    mas as regras do menu batem em cima — "este passo não tem nenhuma resposta prevista" num passo `condicional`,
    "aponta para X que não existe" numa opção que não existe. O cliente veria oito problemas inventados e nenhum
    caminho para publicar.

    A checagem ESTRUTURAL (identificador vazio, identificador repetido) vale para os dois, e por isso ela roda
    antes da bifurcação.
  */
  if (passos.length === 0) {
    return [{ bloqueante: true, passo: null, texto: "O fluxo precisa de pelo menos uma pergunta." }];
  }

  const chaves = new Set<string>();
  const repetidas = new Set<string>();
  for (const p of passos) {
    const chave = p.chave?.trim() ?? "";
    if (chave === "") {
      problemas.push({ bloqueante: true, passo: null, texto: "Há um passo sem identificador." });
      continue;
    }
    if (chaves.has(chave)) repetidas.add(chave);
    chaves.add(chave);
  }
  for (const chave of repetidas) {
    problemas.push({ bloqueante: true, passo: chave, texto: `Há dois passos chamados '${chave}'.` });
  }

  // A bifurcação. Ver o cabeçalho: daqui para baixo as regras são do MENU.
  if (usaBlocos(passos)) {
    return [...problemas, ...problemasDosBlocos(passos)];
  }

  for (const p of passos) {
    const chave = p.chave?.trim() ?? "";
    if (!p.pergunta?.trim()) {
      problemas.push({
        bloqueante: true,
        passo: chave,
        texto: "Este passo não tem pergunta — o contato receberia uma mensagem vazia.",
      });
    }
    if (p.opcoes.length === 0) {
      problemas.push({
        bloqueante: true,
        passo: chave,
        texto:
          "Este passo não tem nenhuma resposta prevista. O contato responderia e o fluxo repetiria a " +
          "pergunta até desistir.",
      });
    }

    const quandos = new Set<string>();
    for (const o of p.opcoes) {
      const quando = o.quando?.trim() ?? "";
      if (quando === "") {
        problemas.push({
          bloqueante: true,
          passo: chave,
          texto: "Uma das respostas previstas está em branco.",
        });
        continue;
      }
      if (quandos.has(quando.toLowerCase())) {
        // Aviso, e não bloqueio: a primeira ganha, o fluxo funciona, e a segunda é só letra morta.
        problemas.push({
          bloqueante: false,
          passo: chave,
          texto: `A resposta '${quando}' aparece duas vezes; só a primeira vale.`,
        });
      }
      quandos.add(quando.toLowerCase());

      if (o.vaiPara && o.vaiPara === chave) {
        problemas.push({
          bloqueante: true,
          passo: chave,
          texto:
            `A resposta '${quando}' aponta para o próprio passo, o que repetiria a mesma pergunta sem fim.`,
        });
      } else if (o.vaiPara && !chaves.has(o.vaiPara)) {
        problemas.push({
          bloqueante: true,
          passo: chave,
          texto: `A resposta '${quando}' aponta para '${o.vaiPara}', que não existe.`,
        });
      }
    }
  }

  // Passo órfão: existe e nenhuma resposta leva até ele. Não quebra nada — o contato simplesmente nunca
  // chega lá — e por isso é aviso. O primeiro passo é a entrada, e não conta como órfão.
  const alcancados = new Set<string>([passos[0]?.chave?.trim() ?? ""]);
  for (const p of passos) for (const o of p.opcoes) if (o.vaiPara) alcancados.add(o.vaiPara);
  for (const p of passos.slice(1)) {
    const chave = p.chave?.trim() ?? "";
    if (chave !== "" && !alcancados.has(chave)) {
      problemas.push({
        bloqueante: false,
        passo: chave,
        texto: "Nenhuma resposta leva a este passo, então o contato nunca vai chegar nele.",
      });
    }
  }

  return problemas;
}

/**
 * O fluxo cabe no degrau 1 — o formulário de quatro campos (§4.1 e §4.5 da spec 12)?
 *
 * ## A regra 2 do §4.5: descer só se couber, e NUNCA converter com perda
 *
 * Subir é sempre possível. Descer, não: um fluxo com três níveis de pergunta não cabe em "cumprimento,
 * pergunta, opções, desistência" — e mostrá-lo lá **truncado** seria a pior coisa possível, porque a pessoa
 * salvaria acreditando que viu o fluxo inteiro e apagaria o resto.
 *
 * Então esta função é o portão, e a tela usa o `porque` para explicar em vez de só desabilitar.
 *
 * ## O que cabe
 *
 * Um passo, ou dois em que o segundo é só informação (uma pergunta e uma saída). Mais que isso é ramo, e
 * ramo é o degrau 2.
 */
export function cabeNoDegrauSimples(
  passos: readonly PassoCompleto[],
): { readonly cabe: true } | { readonly cabe: false; readonly porque: string } {
  if (passos.length === 0) return { cabe: true };
  /*
    Fluxo de BLOCOS não cabe no degrau simples, nunca — nem com um passo só.

    O degrau simples é um formulário de menu: ele lê `passos` e devolve `passos`, e o que ele não conhece se
    perde na volta. Um fluxo com um único bloco `mensagem` passaria na checagem de ramo abaixo, apareceria na
    tela simples como uma pergunta sem opções, e **salvar dali apagaria o `tipo`** — o bloco viraria menu sem
    ninguém tocar em nada.
  */
  if (usaBlocos(passos)) {
    return {
      cabe: false,
      porque:
        "Este fluxo usa blocos (espera, condição, ação, assistente ou sistema externo), e a visão simples é " +
        "um formulário de menu. Ela não conhece esses passos, e salvar por ela os apagaria.",
    };
  }
  if (passos.length > 1) {
    const comRamo = passos.filter((p) => p.opcoes.some((o) => o.vaiPara));
    if (comRamo.length > 0) {
      return {
        cabe: false,
        porque:
          `Este fluxo encadeia perguntas (o passo '${comRamo[0]!.chave}' leva a outro), e a visão simples ` +
          `mostra só uma pergunta. Ela não seria capaz de mostrar o fluxo inteiro — e mostrar pela metade ` +
          `faria você salvar sem o resto.`,
      };
    }
  }
  return { cabe: true };
}

/** O que o degrau 1 edita: quatro campos, e nenhum conceito de passo. */
export interface FormaSimples {
  readonly cumprimento: string;
  readonly pergunta: string;
  readonly opcoes: readonly {
    readonly quando: string;
    readonly rotulo: string;
    readonly filaId: string | null;
    readonly etiquetaId: string | null;
  }[];
}

/**
 * Do modelo para os quatro campos.
 *
 * `cumprimento` e `pergunta` saem do MESMO texto do primeiro passo: o motor tem um campo de texto, e a
 * pessoa pensa em duas coisas. A separação é a primeira linha em branco — que é exactamente como as pessoas
 * escrevem menu de WhatsApp.
 */
export function paraFormaSimples(passos: readonly PassoDoFluxo[]): FormaSimples {
  const primeiro = passos[0];
  if (!primeiro) return { cumprimento: "", pergunta: "", opcoes: [] };

  const partes = primeiro.pergunta.split(/\n\s*\n/);
  const cumprimento = partes.length > 1 ? (partes[0] ?? "").trim() : "";
  const resto = (partes.length > 1 ? partes.slice(1).join("\n\n") : primeiro.pergunta).trim();

  // A pergunta é a primeira linha do resto; as linhas numeradas abaixo são as opções, e elas já estão em
  // `opcoes` — mostrá-las duas vezes faria a pessoa editar uma e não a outra.
  const linhas = resto.split("\n");
  const numeradas = linhas.findIndex((l) => /^\s*\d+\s*[-–.)]/.test(l));
  const pergunta = (numeradas > 0 ? linhas.slice(0, numeradas) : linhas).join("\n").trim();

  return {
    cumprimento,
    pergunta,
    opcoes: primeiro.opcoes.map((o) => {
      const rotulo =
        linhas.find((l) => new RegExp(`^\\s*${o.quando}\\s*[-–.)]`).test(l))?.replace(/^\s*\S+\s*[-–.)]\s*/, "") ??
        "";
      return {
        quando: o.quando,
        rotulo: rotulo.trim(),
        filaId: o.filaId ?? null,
        etiquetaId: o.etiquetaId ?? null,
      };
    }),
  };
}

/**
 * Dos quatro campos para o modelo.
 *
 * **Um modelo só** (regra 3 do §4.5): não existe "fluxo simples" no banco. O que sai daqui é o mesmo
 * `PassoDoFluxo[]` que a lista edita e que `proximoPasso` executa.
 *
 * O texto é remontado com as opções numeradas dentro dele, porque é isso que o contato lê — o motor manda
 * `pergunta` como mensagem, e uma pergunta sem as opções escritas deixaria o contato adivinhando.
 */
export function daFormaSimples(f: FormaSimples): readonly PassoDoFluxo[] {
  const validas = f.opcoes.filter((o) => o.quando.trim() !== "");
  const linhas = validas.map((o) => `${o.quando.trim()} - ${o.rotulo.trim()}`);
  const pergunta = [f.cumprimento.trim(), [f.pergunta.trim(), ...linhas].filter((x) => x !== "").join("\n")]
    .filter((x) => x !== "")
    .join("\n\n");

  return [
    {
      chave: "inicio",
      pergunta,
      opcoes: validas.map((o) => ({
        quando: o.quando.trim(),
        vaiPara: null,
        filaId: o.filaId,
        etiquetaId: o.etiquetaId,
        encerra: true,
      })),
    },
  ];
}

/**
 * --- Modelos prontos (§8b da spec 11, item 6 do §10 da spec 12) ----------------------------------
 *
 * ## A tela em branco é onde a automação morre
 *
 * Ninguém monta o primeiro fluxo do zero. O que acontece de verdade é: a pessoa abre a tela, não sabe o que
 * escrever, fecha, e liga para o suporte — e a partir daí o recurso existe e não é usado.
 *
 * ## Estes modelos são CÓDIGO, e isso é uma decisão de segurança
 *
 * O §8b da spec 11 exige que galeria de modelos nasça com recorte por tenant, e a razão está no
 * `clinia.md` §5c: a galeria do concorrente promete *"modelo salvo pelo seu time"* e mostra dezenas de
 * fluxos com nome de cliente de terceiro. **Galeria é a tela que se esquece de recortar, porque não parece
 * dado de cliente.**
 *
 * A nossa não corre esse risco por construção: **não há tabela**. Estes quatro modelos são constantes deste
 * arquivo — não têm dono, não têm tenant, e não podem vazar o de ninguém.
 *
 * "Salvar o meu fluxo como modelo" **não existe** de propósito, e o motivo é o mesmo: no dia em que existir,
 * ela nasce com `Visao` no `WHERE` e com teste que afirma o recorte, não depois.
 */
export interface ModeloDeFluxo {
  readonly id: string;
  readonly nome: string;
  /** Para quem é. A pessoa escolhe pela situação dela, não pelo nome técnico do fluxo. */
  readonly paraQuem: string;
  /** O que ele NÃO faz. Modelo que promete demais é pior que tela em branco. */
  readonly limite: string | null;
  readonly passos: readonly PassoDoFluxo[];
}

export const MODELOS: readonly ModeloDeFluxo[] = [
  {
    id: "triagem-por-assunto",
    nome: "Triagem por assunto",
    paraQuem: "Quem tem duas ou três filas e quer que cada assunto caia na certa.",
    limite: null,
    passos: [
      {
        chave: "inicio",
        pergunta:
          "Olá! Para agilizar, escolha uma opção:\n\n1 - Agendamento\n2 - Resultado de exame\n3 - Financeiro\n4 - Outro assunto",
        opcoes: [
          { quando: "1", vaiPara: null, filaId: null, etiquetaId: null, encerra: true },
          { quando: "2", vaiPara: null, filaId: null, etiquetaId: null, encerra: true },
          { quando: "3", vaiPara: null, filaId: null, etiquetaId: null, encerra: true },
          { quando: "4", vaiPara: null, filaId: null, etiquetaId: null, encerra: true },
        ],
      },
    ],
  },
  {
    id: "fora-do-horario",
    nome: "Fora do horário",
    paraQuem: "Quem não atende de madrugada e quer que a conversa já chegue organizada de manhã.",
    limite:
      "Ele não promete horário de retorno. Prometer 'respondemos em 1 hora' num fluxo é o jeito mais " +
      "rápido de criar uma reclamação — se você quer prometer, use o horário de atendimento em Ajustes.",
    passos: [
      {
        chave: "inicio",
        pergunta:
          "Olá! Nosso atendimento está fora do horário agora, mas já deixo sua conversa encaminhada.\n\n" +
          "Do que você precisa?\n1 - Agendar ou remarcar\n2 - Dúvida sobre exame\n3 - Outro assunto",
        opcoes: [
          { quando: "1", vaiPara: null, filaId: null, etiquetaId: null, encerra: true },
          { quando: "2", vaiPara: null, filaId: null, etiquetaId: null, encerra: true },
          { quando: "3", vaiPara: null, filaId: null, etiquetaId: null, encerra: true },
        ],
      },
    ],
  },
  {
    id: "primeira-vez-ou-retorno",
    nome: "Primeira vez ou retorno",
    paraQuem: "Quem atende paciente novo e paciente que já é da casa de formas diferentes.",
    limite: null,
    passos: [
      {
        chave: "inicio",
        pergunta: "Olá! Você já é nosso paciente?\n\n1 - Sim, já fui atendido\n2 - É a primeira vez",
        opcoes: [
          { quando: "1", vaiPara: "retorno", filaId: null, etiquetaId: null },
          { quando: "2", vaiPara: null, filaId: null, etiquetaId: null, encerra: true },
        ],
      },
      {
        chave: "retorno",
        pergunta: "Perfeito! O que você precisa hoje?\n\n1 - Marcar retorno\n2 - Resultado de exame\n3 - Outro assunto",
        opcoes: [
          { quando: "1", vaiPara: null, filaId: null, etiquetaId: null, encerra: true },
          { quando: "2", vaiPara: null, filaId: null, etiquetaId: null, encerra: true },
          { quando: "3", vaiPara: null, filaId: null, etiquetaId: null, encerra: true },
        ],
      },
    ],
  },
  {
    id: "so-encaminhar",
    nome: "Só encaminhar, sem menu",
    paraQuem: "Quem prefere que ninguém escolha nada e tudo caia numa fila só — mas quer confirmar o recebimento.",
    limite: "Sem opções, ele não separa assunto nenhum. É o mais simples que existe, e às vezes é o certo.",
    passos: [
      {
        chave: "inicio",
        pergunta: "Olá! Recebemos sua mensagem e já estamos encaminhando para o atendimento. Um instante.",
        opcoes: [{ quando: "1", vaiPara: null, filaId: null, etiquetaId: null, encerra: true }],
      },
    ],
  },
];

/**
 * --- A entrevista (§7 e §7.1 da spec 12) ---------------------------------------------------------
 *
 * ## Ela é DETERMINÍSTICA, e isso não é uma versão pobre do copiloto
 *
 * O §7.1 lista **as quatro perguntas que ninguém lembra de responder sozinho**, e elas são sempre as mesmas.
 * Uma caixa de texto única devolve fluxo com buraco, porque a pessoa descreve o caso feliz e esquece o
 * resto — o valor da entrevista está na **ordem fixa das perguntas**, não em adivinhar.
 *
 * Consequência prática que vale registrar: **isto não precisa de provedor de linguagem.** O copiloto
 * generativo do §7 continua na fila (item 5 do §10) e depende da `OPENAI_API_KEY`; a entrevista entrega a
 * parte que resolve — a pessoa responde em português e sai com os quatro campos preenchidos.
 *
 * ## A regra 5 do §7.2 vale igual, e é a que mais importa
 *
 * *"Ele diz o que NÃO entendeu."* Aqui isso é literal: assunto sem fila escolhida vira opção **sem destino**,
 * que a validação já marca. Nada é preenchido com um palpite plausível — que é o modo de falha que faz um
 * fluxo gerado parecer pronto e não estar.
 */
export interface RespostasDaEntrevista {
  readonly comoCumprimenta: string;
  readonly oQuePergunta: string;
  /** Um assunto por linha, na ordem. A fila é escolhida na tela, não adivinhada aqui. */
  readonly assuntos: readonly { readonly rotulo: string; readonly filaId: string | null }[];
  readonly valeSempre: boolean;
}

export function daEntrevista(r: RespostasDaEntrevista): FormaSimples {
  return {
    cumprimento: r.comoCumprimenta.trim(),
    pergunta: r.oQuePergunta.trim(),
    opcoes: r.assuntos
      .filter((a) => a.rotulo.trim() !== "")
      .map((a, i) => ({
        quando: String(i + 1),
        rotulo: a.rotulo.trim(),
        // `null` quando a pessoa não escolheu: a validação marca, e a tela mostra. Escolher "a primeira
        // fila" aqui seria o palpite plausível que a regra 5 do §7.2 proíbe.
        filaId: a.filaId,
        etiquetaId: null,
      })),
  };
}

/**
 * --- O desenho (degrau 3, §5 da spec 12) ---------------------------------------------------------
 *
 * ## Por que o layout é DERIVADO, e não arrastado
 *
 * A spec 12 §1 corrigiu o enunciado da `0020`: *"restrinja o modelo, não o editor"*. E o modelo restrito
 * tem uma consequência que economiza o editor inteiro: **o fluxo só aponta para a frente**, então ele é uma
 * árvore, e árvore tem layout canônico.
 *
 * Isso resolve de graça três das cinco guardas do §5.2:
 *
 * | Guarda | Como fica |
 * |---|---|
 * | "ligação para trás não conecta" | **não há ligação para desenhar** — a posição vem do modelo |
 * | "ponta solta fica vermelha" | opção sem destino e sem fila é problema, e a validação já a lista |
 * | "teto de passos" | o `CHECK` da `0020` e a contagem no cabeçalho |
 *
 * E o custo declarado do §8 desaparece junto: **nenhuma biblioteca de canvas**, nada carregado sob demanda,
 * nenhuma cor nova fora da palheta. O que sobra é SVG com posições calculadas aqui — testável sem navegador,
 * que é a razão de estar neste módulo puro e não na tela.
 *
 * ## O que este desenho NÃO faz, e é honesto dizer
 *
 * Não arrasta nó. Arrastar só faz sentido quando a posição carrega informação, e aqui ela não carrega: dois
 * fluxos com os mesmos passos desenham igual, sempre. Guardar posição seria guardar preferência de layout
 * como se fosse dado do fluxo.
 */
export interface NoDoDesenho {
  readonly chave: string;
  /** Coluna (profundidade) e linha, em unidades de grade — a tela multiplica pelo tamanho da carta. */
  readonly coluna: number;
  readonly linha: number;
  readonly pergunta: string;
  readonly quantasOpcoes: number;
  /** `true` quando nenhuma opção leva a ele — o passo órfão do §3.2. */
  readonly orfao: boolean;
  /**
   * O tipo do bloco, ausente quando o passo é menu.
   *
   * A caixa precisa dizer o que ela É: um `esperar` e um `condicional` desenhados iguais fazem o canvas parecer
   * um fluxo linear que não é.
   */
  readonly tipo?: TipoDePasso;
}

export interface LigacaoDoDesenho {
  readonly de: string;
  readonly para: string;
  readonly rotulo: string;
}

/** Uma saída que termina o fluxo: vai para fila, e não para outro passo. */
export interface SaidaDoDesenho {
  readonly de: string;
  readonly rotulo: string;
  readonly coluna: number;
  readonly linha: number;
  readonly filaId: string | null;
  /** `true` quando a opção não tem destino nenhum — nem passo, nem fila. É ponta solta. */
  readonly solta: boolean;
}

export interface Desenho {
  readonly nos: readonly NoDoDesenho[];
  readonly ligacoes: readonly LigacaoDoDesenho[];
  readonly saidas: readonly SaidaDoDesenho[];
  readonly colunas: number;
  readonly linhas: number;
}

/**
 * Calcula o desenho.
 *
 * Percurso em largura a partir do primeiro passo: a **coluna** é a distância até a entrada, e a **linha** é
 * a ordem de descoberta dentro daquela coluna. É o layout que uma pessoa desenharia à mão, e o que faz um
 * fluxo de dez passos continuar legível.
 *
 * Passo inalcançável entra no fim, marcado — esconder o órfão faria a pessoa procurar no desenho um passo
 * que a lista diz que existe.
 */
/**
 * As ligações que SAEM de um passo, seja ele menu ou bloco.
 *
 * ## O defeito que esta função corrige
 *
 * `desenhar` seguia **só** `opcoes[].vaiPara`. Num fluxo de blocos, onde os destinos moram em `proximo`,
 * `entao`, `senao`, `aoEstourar`, `aoFalhar` e `aoVirVazio`, o resultado era um desenho sem nenhuma ligação:
 * todas as caixas na primeira coluna, todas marcadas como órfãs. O canvas não dava erro — dava uma pilha.
 *
 * O rótulo importa tanto quanto o destino: é ele que faz o desenho responder *"por que esta seta existe?"*, e
 * é o que separa a seta de "não casou nenhuma condição" da de "o sistema não respondeu".
 */
export function arestasDoPasso(
  p: PassoCompleto,
): readonly { readonly para: string; readonly rotulo: string }[] {
  const tipo = p.tipo ?? "menu";
  if (tipo === "menu") {
    return p.opcoes.flatMap((o) => (o.vaiPara ? [{ para: o.vaiPara, rotulo: o.quando }] : []));
  }
  const arestas: { para: string; rotulo: string }[] = [];
  const juntar = (para: string | null | undefined, rotulo: string) => {
    if (para) arestas.push({ para, rotulo });
  };
  if (tipo === "condicional") {
    juntar(p.entao, "quando vale");
    juntar(p.senao, "quando não vale");
  } else {
    juntar(p.proximo, "depois");
  }
  if (tipo === "agente") juntar(p.agente?.aoEstourar, "sem saída");
  if (tipo === "ferramenta") {
    juntar(p.ferramenta?.aoFalhar, "falhou");
    juntar(p.ferramenta?.aoVirVazio, "veio vazio");
  }
  if (tipo === "reengajar") {
    juntar(p.reengajar?.aoDesistir, "desistiu");
    juntar(p.reengajar?.aoResponder, "respondeu");
  }
  return arestas;
}

export function desenhar(passos: readonly PassoCompleto[]): Desenho {
  if (passos.length === 0) return { nos: [], ligacoes: [], saidas: [], colunas: 0, linhas: 0 };

  const porChave = new Map(passos.map((p) => [p.chave, p]));
  const coluna = new Map<string, number>();
  const fila: string[] = [passos[0]!.chave];
  coluna.set(passos[0]!.chave, 0);

  // Largura primeiro. `visitados` impede laço de travar o cálculo — o modelo já proíbe voltar, mas um
  // desenho que trava é pior que um desenho errado, e o custo de proteger é uma linha.
  const visitados = new Set<string>([passos[0]!.chave]);
  while (fila.length > 0) {
    const chave = fila.shift()!;
    const p = porChave.get(chave);
    if (!p) continue;
    for (const a of arestasDoPasso(p)) {
      if (!porChave.has(a.para) || visitados.has(a.para)) continue;
      visitados.add(a.para);
      coluna.set(a.para, (coluna.get(chave) ?? 0) + 1);
      fila.push(a.para);
    }
  }

  const alcancados = new Set<string>([passos[0]!.chave]);
  for (const p of passos) for (const a of arestasDoPasso(p)) alcancados.add(a.para);

  const proximaLinha = new Map<number, number>();
  const nos: NoDoDesenho[] = [];
  // Ordem: por coluna e, dentro dela, a ordem em que aparecem nos passos. Determinístico — o mesmo fluxo
  // desenha igual sempre, e isso é o que permite comparar dois desenhos com o olho.
  const ordenados = [...passos].sort((a, b) => (coluna.get(a.chave) ?? 99) - (coluna.get(b.chave) ?? 99));
  for (const p of ordenados) {
    const c = coluna.get(p.chave) ?? Math.max(0, ...[...coluna.values()]) + 1;
    const l = proximaLinha.get(c) ?? 0;
    proximaLinha.set(c, l + 1);
    nos.push({
      chave: p.chave,
      coluna: c,
      linha: l,
      pergunta: p.pergunta,
      // O bloco não tem opções, e o número serve para a caixa dizer "3 respostas". Nos blocos, o que
      // interessa é quantas SETAS saem — é a mesma pergunta com outra fonte.
      quantasOpcoes: (p.tipo ?? "menu") === "menu" ? p.opcoes.length : arestasDoPasso(p).length,
      ...((p.tipo ?? "menu") === "menu" ? {} : { tipo: p.tipo }),
      orfao: !alcancados.has(p.chave),
    });
  }

  const ligacoes: LigacaoDoDesenho[] = [];
  const saidas: SaidaDoDesenho[] = [];
  for (const p of passos) {
    const c = (coluna.get(p.chave) ?? 0) + 1;

    /*
      Bloco: as setas vêm de `arestasDoPasso`, e um destino vazio **não** vira "saída para fila".

      A diferença é real: no menu, opção sem `vaiPara` encaminha para uma fila — é uma saída do fluxo com
      destino. No bloco, `proximo` vazio significa que o fluxo terminou ali, e desenhar uma caixa de fila
      inventaria um encaminhamento que não acontece.
    */
    if ((p.tipo ?? "menu") !== "menu") {
      for (const a of arestasDoPasso(p)) {
        if (porChave.has(a.para)) ligacoes.push({ de: p.chave, para: a.para, rotulo: a.rotulo });
      }
      continue;
    }

    for (const o of p.opcoes) {
      if (o.vaiPara && porChave.has(o.vaiPara)) {
        ligacoes.push({ de: p.chave, para: o.vaiPara, rotulo: o.quando });
        continue;
      }
      const l = proximaLinha.get(c) ?? 0;
      proximaLinha.set(c, l + 1);
      saidas.push({
        de: p.chave,
        rotulo: o.quando,
        coluna: c,
        linha: l,
        filaId: o.filaId ?? null,
        // Sem passo e sem fila: o contato escolhe e cai na fila padrão do canal. Não é erro, mas é a coisa
        // que a pessoa mais esquece de configurar — e no desenho ela fica visível.
        solta: !o.vaiPara && !o.filaId,
      });
    }
  }

  return {
    nos,
    ligacoes,
    saidas,
    colunas: Math.max(1, ...nos.map((n) => n.coluna + 1), ...saidas.map((s) => s.coluna + 1)),
    linhas: Math.max(1, ...[...proximaLinha.values()]),
  };
}

/**
 * --- Os blocos (spec 11 §3.4 e §8) ---------------------------------------------------------------
 *
 * ## O tipo é OPCIONAL, e isso é o que torna a mudança segura
 *
 * Passo sem `tipo` é `menu` — que é o que todo fluxo gravado até a `0024` tem. A leitura de um fluxo antigo
 * não muda de comportamento, e nenhuma migração de `jsonb` foi necessária.
 *
 * ## Por que `agente` entra AGORA, e não depois
 *
 * §8.3, com estas palavras: *"é barato agora e caro depois. Se o motor de fluxo nascer sem a noção de que um
 * passo pode ser não determinístico, encaixar isso depois exige mexer em execução, versionamento e trilha ao
 * mesmo tempo."*
 *
 * O desenho implícito do Hugi era binário — ou o agente atende, ou a automação atende. **É falso.** O fluxo
 * determina o caminho; o agente resolve o passo que precisa de linguagem.
 *
 * ## O que NÃO entra, e a ausência é deliberada
 *
 * **`ferramenta`** (chamada HTTP externa, §3.5 e §8c). Ela exige superfície de configuração própria — URL,
 * segredo, timeout, recusa de rede interna — e as **duas saídas obrigatórias** (`falha` e `vazio`), que o
 * §8c trata como cidadãs de primeira classe. Metade disso produziria um passo que trava a conversa quando o
 * ERP do cliente cai.
 *
 * Não está no tipo de propósito: estender uma união depois é seguro; ter um tipo que o motor não executa
 * não é.
 */
export type TipoDePasso =
  | "menu"
  | "mensagem"
  | "perguntar"
  | "condicional"
  | "esperar"
  | "acao"
  | "agente"
  | "ferramenta"
  | "reengajar"
  | "fim";

/**
 * Quando o fluxo começa (§3.1).
 *
 * ## Os três primeiros são do MENU; os três últimos, do motor de blocos
 *
 * `primeira_mensagem` e `fora_do_horario` são respostas a **uma mensagem que chega**, e é o que
 * `proximoPasso` sabe fazer. Os três novos são diferentes em natureza:
 *
 * | Gatilho | O que ele observa | Parâmetro |
 * |---|---|---|
 * | `mensagem_contendo` | qualquer mensagem recebida, não só a primeira | o texto |
 * | `etiqueta_aplicada` | a equipe etiquetou o contato | o id da etiqueta |
 * | `motivo_de_encerramento` | o atendimento foi finalizado com aquele motivo | o id do motivo |
 * | `oportunidade_movida` | um card do funil ENTROU naquela etapa (spec 10 §4.4) | o id da etapa |
 *
 * Os dois últimos **não são mensagem** — são coisas que acontecem no atendimento. Por isso não cabem em
 * `proximoPasso`, que é reativa por mensagem, e por isso quem os atende é o motor de blocos.
 */
export type Gatilho =
  | "nunca"
  | "primeira_mensagem"
  | "fora_do_horario"
  | "mensagem_contendo"
  | "etiqueta_aplicada"
  | "motivo_de_encerramento"
  | "oportunidade_movida";

/** Os gatilhos que exigem parâmetro. Sem ele, `mensagem_contendo` casaria com toda mensagem. */
export const GATILHOS_COM_VALOR: readonly Gatilho[] = [
  "mensagem_contendo",
  "etiqueta_aplicada",
  "motivo_de_encerramento",
  "oportunidade_movida",
];

/**
 * Os gatilhos que **só o motor de blocos atende**.
 *
 * O menu (`proximoPasso`) trata `nunca` e `fora_do_horario`, e mais nada: fora esses dois ele cai
 * direto no ramo da PRIMEIRA MENSAGEM. Um fluxo de menu com `mensagem_contendo` não deixa de rodar —
 * ele roda **ignorando a palavra**, abrindo o menu na primeira mensagem de qualquer conversa. A
 * condição que a pessoa configurou some sem aviso, que é pior que não funcionar.
 */
export const GATILHOS_SO_COM_BLOCOS: readonly Gatilho[] = [
  "mensagem_contendo",
  "etiqueta_aplicada",
  "motivo_de_encerramento",
  "oportunidade_movida",
];

/**
 * O gatilho combina com a FORMA do fluxo?
 *
 * Mora aqui, no módulo puro, porque tem **três** consumidores que não podem divergir: a tela (que
 * desabilita a opção), `publicarFluxo` (que é o portão) e a prova. Até 05/09 a regra existia só na
 * tela, como um `soComBlocos` escrito à mão — então bastava escolher o gatilho com blocos e apagar
 * os blocos depois para chegar à combinação que o motor não honra, e nada recusava.
 *
 * ⚠️ **A recusa é do PAR, e a frase diz os dois lados** — "este gatilho não serve" sozinho mandaria
 * a pessoa trocar o gatilho quando trocar a forma também resolve.
 */
export function gatilhoCombinaComAForma(
  quando: Gatilho,
  passos: readonly PassoCompleto[],
): { readonly serve: true } | { readonly serve: false; readonly porque: string } {
  if (!GATILHOS_SO_COM_BLOCOS.includes(quando)) return { serve: true };
  if (usaBlocos(passos)) return { serve: true };
  return {
    serve: false,
    porque:
      "Este gatilho vale em qualquer momento da conversa, e um menu só abre na PRIMEIRA mensagem — " +
      "publicado assim, ele ignoraria a condição e apareceria em toda conversa nova. Acrescente um " +
      "passo que não seja menu, ou escolha 'Na primeira mensagem' / 'Fora do horário'.",
  };
}

/** O que aconteceu, do ponto de vista do gatilho. */
export interface EventoDeGatilho {
  readonly tipo: "mensagem_recebida" | "etiqueta_aplicada" | "atendimento_finalizado" | "oportunidade_movida";
  /** O texto da mensagem, quando o evento é mensagem. */
  readonly texto?: string | null;
  /** O id da etiqueta ou do motivo, conforme o evento. */
  readonly valor?: string | null;
  /** A mensagem criou o atendimento? Só `primeira_mensagem` olha isso. */
  readonly criouAtendimento?: boolean;
  readonly dentroDoHorario?: boolean | null;
}

/**
 * Este evento dispara este fluxo?
 *
 * **Pura, e é o ponto único de decisão.** Duas leituras de "o gatilho casou" — uma no caminho de mensagem e
 * outra no caminho de evento — divergiriam, e a divergência apareceria como fluxo que dispara em um lugar e
 * não no outro, sem nada no log dizendo qual dos dois está certo.
 *
 * ## `mensagem_contendo` compara SEM acento e em minúsculas
 *
 * O cliente escreve "orçamento" e o contato escreve "orcamento". Comparação literal falharia na metade dos
 * casos reais, e o cliente concluiria que o gatilho não funciona — o que é pior que ele não existir.
 */
export function gatilhoServe(
  fluxo: { readonly quando: Gatilho; readonly quandoValor?: string | null },
  evento: EventoDeGatilho,
): boolean {
  if (fluxo.quando === "nunca") return false;

  if (fluxo.quando === "primeira_mensagem") {
    return evento.tipo === "mensagem_recebida" && evento.criouAtendimento === true;
  }

  if (fluxo.quando === "fora_do_horario") {
    // `null` = o tenant nunca cadastrou horário. Trata-se como fora, pelo mesmo motivo de `automacao.ts`:
    // um interruptor que não faz efeito e não explica é o pior estado possível.
    return evento.tipo === "mensagem_recebida" && (evento.dentroDoHorario ?? false) === false;
  }

  const valor = fluxo.quandoValor?.trim();
  if (!valor) return false;

  if (fluxo.quando === "mensagem_contendo") {
    if (evento.tipo !== "mensagem_recebida") return false;
    return semAcento(evento.texto ?? "").includes(semAcento(valor));
  }

  if (fluxo.quando === "etiqueta_aplicada") {
    return evento.tipo === "etiqueta_aplicada" && evento.valor === valor;
  }

  // O card ENTROU na etapa — ao ser movido, criado nela, ganho ou perdido (as saídas também são etapas).
  if (fluxo.quando === "oportunidade_movida") {
    return evento.tipo === "oportunidade_movida" && evento.valor === valor;
  }

  return evento.tipo === "atendimento_finalizado" && evento.valor === valor;
}

function semAcento(s: string): string {
  return s
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

/** Uma comparação do passo `condicional`. */
export interface Comparacao {
  /** O nome da variável coletada, ou um campo do atendimento (`fila`, `canal`, `etiqueta`). */
  readonly variavel: string;
  readonly operador: "igual" | "contem" | "existe" | "nao_existe";
  readonly valor?: string | null;
}

/**
 * O que um bloco de agente declara (§8.3).
 *
 * As quatro coisas que tornam isso seguro, e nenhuma é opcional:
 *
 * 1. **`objetivo`** limita o escopo — o agente naquele passo não é o assistente geral;
 * 2. **`saidasEsperadas`** fecha o contrato — o bloco só continua se a saída for uma das declaradas, e é o
 *    que permite ligar o passo seguinte;
 * 3. **`maxTurnos`** é o mesmo `max_turnos_sem_humano` da `0017` — reusar, não inventar;
 * 4. **`aoEstourar`** aponta para gente, e é obrigatório.
 */
export interface BlocoDeAgente {
  /**
   * QUAL agente assume este passo (0057). É a relação que o owner pediu — *"vamos precisar relacionar
   * eles a fluxos... para boas-vindas, para outras coisas"*: o fluxo de boas-vindas chama o agente da
   * recepção, o de cobrança chama o financeiro. Com ele, o motor carrega as instruções, os
   * procedimentos e o conhecimento DAQUELE agente antes do objetivo do passo. Sem ele (fluxo
   * anterior à 0057, ou "nenhum"), o passo roda só com o objetivo, como sempre rodou.
   */
  readonly agenteId?: string | null;
  readonly objetivo: string;
  readonly saidasEsperadas: readonly string[];
  readonly maxTurnos: number;
  /** Para onde vai quando estoura. `null` = fila de desistência do fluxo — nunca "continua". */
  readonly aoEstourar: string | null;
}

/**
 * O que o passo `acao` faz. São as operações que **um atendente já faz na tela** — nenhum efeito novo.
 *
 * ## `abrir_no_funil`: a promessa que o modelo fazia e não cumpria
 *
 * O modelo de fotografia se descreve como *"triagem por tipo de trabalho, coleta de data e cidade, e
 * **o card no funil**"* — e o fluxo dele **não criava card nenhum**. Um fluxo não tinha como: as
 * ações eram três, e nenhuma tocava o funil. O card só nascia de campanha, anúncio, importação ou do
 * botão "Abrir no funil" do inbox.
 *
 * Ela **compõe `abrirOportunidade`**, a mesma função daqueles quatro caminhos — e herda de graça a
 * regra que mais importa: **uma aberta por contato POR QUADRO**. Rodar duas vezes não cria dois
 * cards; a segunda re-aponta a primeira. É o que torna o passo seguro num fluxo que pode ser
 * reexecutado.
 *
 * ## `dar_como_perdida`, e por que ela entrou aqui e não como tipo de passo próprio
 *
 * O fluxo de reengajamento pergunta se a pessoa ainda quer, e quando ela diz que não, o card dela
 * ficava **aberto para sempre** no quadro — a negociação fantasma que a spec 10 §4.1 já descreve para
 * o encerramento de conversa. Quem monta o fluxo tinha de lembrar de ir ao funil arrastar o card à
 * mão, e ninguém lembra.
 *
 * Ela é **composição**: chama `desfechoDaOportunidadeAoFinalizar`, a mesma função que o painel de
 * Encerrar usa. Não há `UPDATE` de oportunidade dentro do motor, e por isso as regras que valem para
 * a pessoa valem para o fluxo — inclusive a de que **perder exige motivo**.
 */
export interface AcaoInterna {
  readonly tipo: "etiquetar" | "mover_fila" | "finalizar" | "dar_como_perdida" | "abrir_no_funil";
  readonly etiquetaId?: string | null;
  readonly filaId?: string | null;
  /** O motivo de ENCERRAMENTO do atendimento (`finalizar`). Não é o motivo de perda. */
  readonly motivoId?: string | null;
  /**
   * O motivo de PERDA do card (`dar_como_perdida`). Campo próprio, e não o `motivoId` reusado: são
   * dois catálogos diferentes (`motivos` e `motivos_de_perda`), e um passo pode um dia fazer as duas
   * coisas. Reusar o campo faria o id de um catálogo ser gravado como se fosse do outro.
   */
  readonly motivoDePerdaId?: string | null;
  /**
   * O título do card, em `abrir_no_funil`. Sem ele, `abrirOportunidade` usa o padrão dela.
   *
   * ⚠️ **Sem quadro escolhido, de propósito.** O card nasce no quadro **padrão** do tenant, que é
   * onde todo lead automático nasce (campanha, anúncio, importação). Deixar o fluxo escolher o
   * quadro criaria a primeira porta pela qual um lead automático entra fora do padrão — e o dia em
   * que alguém arquivasse aquele quadro, o passo pararia de funcionar em silêncio.
   */
  readonly tituloDoCard?: string | null;
}

/**
 * O passo que fala com um sistema de fora (§3.5 e §8c).
 *
 * ## As duas saídas, e por que `vazio` não pode ser `falha`
 *
 * | Situação | O que aconteceu | O que o cliente deve ler |
 * |---|---|---|
 * | **falha** | o sistema não respondeu, ou respondeu erro | cabe tentar de novo, ou escalar |
 * | **vazio** | o sistema respondeu **"não há nada"** | "não temos horário essa semana" |
 *
 * Sem `vazio` explícito, o fluxo trata ausência de resultado como erro — e o contato recebe "ocorreu um
 * problema" quando a resposta certa era uma informação. É mentira dita pelo produto, e só aparece com
 * tráfego real.
 *
 * **As duas são obrigatórias**, como o `senao` do condicional, e pelo mesmo motivo: saída implícita é fluxo
 * que engole gente.
 */
export interface BlocoDeFerramenta {
  /** O nome que a tela mostra. É do cliente, e o fluxo o usa na trilha. */
  readonly nome: string;
  readonly url: string;
  readonly metodo: "GET" | "POST";
  /** Segundos. Tem teto — chamada lenta dentro de fluxo é uma fila de gente parada. */
  readonly timeoutSeg: number;
  /** Onde guardar o que voltou, para o condicional seguinte poder ler. */
  readonly guardaEm?: string | null;
  /** **Obrigatória.** Para onde ir quando o sistema não respondeu. */
  readonly aoFalhar: string | null;
  /** **Obrigatória.** Para onde ir quando o sistema respondeu "não há nada". */
  readonly aoVirVazio: string | null;
}

/**
 * Este fluxo é de blocos?
 *
 * Um passo **sem** `tipo`, ou com `tipo = 'menu'`, é menu — foi assim que a `0025` entrou sem migrar `jsonb`
 * nenhum. Basta um passo com tipo de bloco para o fluxo inteiro ser do motor novo: um fluxo meio-menu
 * meio-blocos não existe, e tratá-lo como menu deixaria os blocos silenciosamente sem executar.
 *
 * Mora no módulo PURO porque a validação da tela precisa dela, e a tela não pode importar a borda (que fala
 * com `fetch`, com o cofre e com o Postgres).
 */
export function usaBlocos(passos: readonly PassoCompleto[]): boolean {
  return passos.some((p) => p.tipo !== undefined && p.tipo !== "menu");
}

/**
 * Reengajamento (§7): recuperar quem sumiu, com as três regras que ele tem de próprio.
 *
 * ## Por que isto é um bloco, e não um fluxo montado com espera e condicional
 *
 * §7: *"um fluxo de reengajamento montado à mão erra sempre no mesmo ponto — não cancela quando a pessoa
 * responde, e a cliente que respondeu 'já resolvi' recebe mais três lembretes. Isso não é bug do cliente, é
 * buraco do produto."*
 *
 * Então o cancelamento **não é configurável**: mensagem do contato cancela, sempre. O que o cliente escolhe é
 * para onde ir depois de cancelar — não *se* cancela.
 *
 * ## `aoDesistir` é obrigatório, e desistir também
 *
 * Um reengajamento sem fim é o produto perseguindo alguém. Depois da última tentativa a conversa vai para o
 * destino declarado — que em quase todo caso é gente, ou uma etiqueta de "não respondeu".
 */
export interface BlocoDeReengajamento {
  /** Quantas mensagens no máximo. Tem teto — ver `TETO_DE_TENTATIVAS`. */
  readonly tentativas: number;
  readonly intervaloMin: number;
  /** O texto de cada tentativa. Uma só é repetida; várias são usadas em ordem, e a última se repete. */
  readonly mensagens: readonly string[];
  /** **Obrigatório.** Para onde ir quando as tentativas acabam. Desistir não é opcional. */
  readonly aoDesistir: string | null;
  /** Para onde ir quando o contato responde. Vazio segue por `proximo`. */
  readonly aoResponder?: string | null;
}

/**
 * Tetos do reengajamento.
 *
 * Cinco tentativas é o limite do que se distingue de perseguição; o intervalo mínimo de uma hora existe pelo
 * mesmo motivo — três mensagens em dez minutos é o que faz o contato bloquear o número, e o número é o do
 * cliente.
 */
export const TETO_DE_TENTATIVAS = 5;
export const INTERVALO_MINIMO_MIN = 60;

/** Teto do timeout do passo de ferramenta. Ver `BlocoDeFerramenta.timeoutSeg`. */
export const TETO_DE_TIMEOUT_SEG = 15;

export interface PassoCompleto extends PassoDoFluxo {
  readonly tipo?: TipoDePasso;
  /** `perguntar`: onde guardar a resposta. */
  readonly guardaEm?: string | null;
  /** `condicional`: as comparações e para onde vai o `else`. */
  readonly se?: readonly Comparacao[];
  readonly entao?: string | null;
  /** **Obrigatório** no condicional. Sem `else`, o fluxo engole gente (§3.4). */
  readonly senao?: string | null;
  /** `esperar`: quantos minutos. Tem teto — ver `TETO_DE_ESPERA_MIN`. */
  readonly esperarMin?: number | null;
  readonly acao?: AcaoInterna | null;
  readonly agente?: BlocoDeAgente | null;
  readonly ferramenta?: BlocoDeFerramenta | null;
  readonly reengajar?: BlocoDeReengajamento | null;
  /** `mensagem`, `perguntar`, `esperar`, `acao`, `agente`: o passo seguinte. */
  readonly proximo?: string | null;
}

/**
 * Os três tetos do §6, como constantes nomeadas.
 *
 * Com condicional e espera, "só aponta para a frente" **deixa de valer** — dá para montar A → B → A. As três
 * guardas precisam existir **juntas**: teto de passos pega o laço rápido, teto de duração pega o laço lento
 * (A espera 1 dia → B → A), e o de reentrada pega o gatilho disparando três vezes.
 */
export const TETO_DE_PASSOS_POR_EXECUCAO = 50;
export const TETO_DE_DURACAO_HORAS = 72;
/** Espera de 30 dias é um fluxo que ninguém lembra que existe, e o contato preso dentro dele. */
export const TETO_DE_ESPERA_MIN = 60 * 24 * 7;

/** O que o motor deve fazer com este passo. */
export type AcaoDoBloco =
  | { readonly acao: "mandar"; readonly texto: string; readonly proximo: string | null }
  | { readonly acao: "perguntar_e_guardar"; readonly texto: string; readonly guardaEm: string; readonly proximo: string | null }
  | { readonly acao: "ir_para"; readonly passo: string; readonly porque: string }
  | { readonly acao: "esperar"; readonly minutos: number; readonly proximo: string | null }
  | { readonly acao: "executar"; readonly interna: AcaoInterna; readonly proximo: string | null }
  | { readonly acao: "chamar_agente"; readonly bloco: BlocoDeAgente; readonly proximo: string | null }
  | {
      readonly acao: "chamar_ferramenta";
      readonly bloco: BlocoDeFerramenta;
      readonly proximo: string | null;
    }
  | {
      readonly acao: "reengajar";
      readonly texto: string;
      /** Qual tentativa é esta, contando de 1. Vai para a trilha. */
      readonly tentativa: number;
      readonly minutos: number;
    }
  | { readonly acao: "terminar"; readonly porque: string }
  | { readonly acao: "estourou"; readonly porque: string };

/**
 * Avalia UM bloco. Pura, e é o coração do motor.
 *
 * ## Por que separar isto de `proximoPasso`
 *
 * `proximoPasso` responde *"o contato escreveu; o que o menu faz?"* — ela é reativa e por mensagem. O motor
 * de blocos é diferente: ele **anda sozinho** por vários passos até precisar de algo (mandar uma mensagem,
 * esperar, ou uma resposta). Misturar as duas faria a função do menu ganhar um laço, e o menu é o caminho de
 * 90% dos clientes.
 */
export function avaliarBloco(
  passo: PassoCompleto,
  contexto: {
    readonly variaveis: Readonly<Record<string, string>>;
    readonly respostaDoCliente: string | null;
    readonly filaId: string | null;
    readonly canal: string | null;
    readonly etiquetas: readonly string[];
    /** Quantas tentativas de reengajamento já saíram, por passo. Estado de controle, não conteúdo. */
    readonly tentativas?: Readonly<Record<string, number>>;
  },
): AcaoDoBloco {
  const tipo = passo.tipo ?? "menu";

  if (tipo === "fim") return { acao: "terminar", porque: "O fluxo chegou ao fim." };

  if (tipo === "mensagem") {
    return { acao: "mandar", texto: passo.pergunta, proximo: passo.proximo ?? null };
  }

  if (tipo === "perguntar") {
    const guardaEm = passo.guardaEm?.trim();
    if (!guardaEm) {
      // Sem nome de variável, a resposta do cliente é jogada fora e o condicional seguinte nunca casa. É
      // problema de fluxo, e a validação o marca — aqui a saída é ir para gente, não seguir em silêncio.
      return { acao: "estourou", porque: `O passo '${passo.chave}' pergunta e não diz onde guardar a resposta.` };
    }
    if (contexto.respostaDoCliente === null) {
      return { acao: "perguntar_e_guardar", texto: passo.pergunta, guardaEm, proximo: passo.proximo ?? null };
    }
    // Já respondeu: o motor grava e segue. Quem grava é o motor, não esta função — ela é pura.
    return {
      acao: "ir_para",
      passo: passo.proximo ?? "",
      porque: `Coletou '${guardaEm}'.`,
    };
  }

  if (tipo === "condicional") {
    const comparacoes = passo.se ?? [];
    if (comparacoes.length === 0 || !passo.senao) {
      /*
        `else` obrigatório (§3.4). Sem ele, o fluxo **engole gente**: quem não casa nenhuma condição fica
        parado num passo que não decide, e ninguém descobre até alguém reclamar.

        A validação recusa publicar assim; aqui a saída é ir para gente — porque um fluxo publicado antes
        desta regra existir continuaria rodando.
      */
      return {
        acao: "estourou",
        porque: `O passo '${passo.chave}' é condicional e não tem saída para "nenhuma das opções".`,
      };
    }

    // TODAS as comparações precisam casar. "Alguma" seria mais permissivo e menos previsível — e quem monta
    // condicional espera "e", não "ou". Duas condições em "ou" se montam com dois condicionais.
    const casou = comparacoes.every((c) => comparar(c, contexto));
    return {
      acao: "ir_para",
      passo: (casou ? passo.entao : passo.senao) ?? "",
      porque: casou
        ? `Todas as condições casaram: ${comparacoes.map(descrever).join(" e ")}.`
        : `Não casou: ${comparacoes.map(descrever).join(" e ")}.`,
    };
  }

  if (tipo === "esperar") {
    const minutos = passo.esperarMin ?? 0;
    if (minutos <= 0 || minutos > TETO_DE_ESPERA_MIN) {
      return {
        acao: "estourou",
        porque:
          `O passo '${passo.chave}' espera ${minutos} minutos, e o limite é ${TETO_DE_ESPERA_MIN} ` +
          `(sete dias). Espera longa é um fluxo que ninguém lembra que existe, com o contato preso dentro.`,
      };
    }
    return { acao: "esperar", minutos, proximo: passo.proximo ?? null };
  }

  if (tipo === "acao") {
    if (!passo.acao) {
      return { acao: "estourou", porque: `O passo '${passo.chave}' não diz qual ação executar.` };
    }
    return { acao: "executar", interna: passo.acao, proximo: passo.proximo ?? null };
  }

  if (tipo === "agente") {
    const bloco = passo.agente;
    if (!bloco || !bloco.objetivo?.trim() || bloco.saidasEsperadas.length === 0) {
      /*
        As quatro coisas do §8.3 não são opcionais. Um bloco de agente sem `objetivo` é o assistente geral
        respondendo no meio de um caminho rígido — que é exatamente o que o bloco existe para não ser. E sem
        `saidasEsperadas` não há contrato: não dá para ligar o passo seguinte.
      */
      return {
        acao: "estourou",
        porque:
          `O passo '${passo.chave}' chama o assistente sem objetivo ou sem saídas esperadas. Sem os dois, ` +
          `ele volta a ser o assistente geral no meio de um caminho que precisa ser previsível.`,
      };
    }
    return { acao: "chamar_agente", bloco, proximo: passo.proximo ?? null };
  }

  if (tipo === "ferramenta") {
    const bloco = passo.ferramenta;
    if (!bloco?.url?.trim()) {
      return { acao: "estourou", porque: `O passo '${passo.chave}' chama um sistema e não tem endereço.` };
    }
    if (!bloco.aoFalhar || !bloco.aoVirVazio) {
      /*
        As duas saídas do §8c são obrigatórias, e a recusa acontece aqui **e** na validação. Aqui porque um
        fluxo publicado antes desta regra continuaria rodando, e ele não pode seguir por `proximo` quando a
        chamada falha: o passo seguinte foi montado pressupondo um resultado que não existe.
      */
      return {
        acao: "estourou",
        porque:
          `O passo '${passo.chave}' fala com um sistema de fora e não declara para onde ir quando ele falha ` +
          `ou quando volta vazio. As duas saídas são obrigatórias.`,
      };
    }
    if (bloco.timeoutSeg <= 0 || bloco.timeoutSeg > TETO_DE_TIMEOUT_SEG) {
      return {
        acao: "estourou",
        porque:
          `O passo '${passo.chave}' espera até ${bloco.timeoutSeg}s pelo sistema, e o limite é ` +
          `${TETO_DE_TIMEOUT_SEG}s. Chamada lenta dentro de fluxo é uma fila de gente parada.`,
      };
    }
    return { acao: "chamar_ferramenta", bloco, proximo: passo.proximo ?? null };
  }

  if (tipo === "reengajar") {
    const bloco = passo.reengajar;
    if (!bloco || bloco.mensagens.length === 0 || !bloco.mensagens.some((m) => m.trim() !== "")) {
      return { acao: "estourou", porque: `O passo '${passo.chave}' reengaja e não tem mensagem para mandar.` };
    }
    if (!bloco.aoDesistir) {
      return {
        acao: "estourou",
        porque:
          `O passo '${passo.chave}' reengaja e não diz o que fazer quando as tentativas acabam. Desistir é ` +
          `obrigatório — reengajamento sem fim é o produto perseguindo alguém.`,
      };
    }

    /*
      ## O CONTATO RESPONDEU: cancela, e isto vem antes de tudo

      É a regra do §7 que não é configurável. A ordem importa: se a checagem de tentativas viesse primeiro,
      quem respondeu na última tentativa seria mandado para `aoDesistir` — ou seja, tratado como quem não
      respondeu, que é o defeito exato que o bloco existe para não ter.
    */
    if (contexto.respostaDoCliente !== null) {
      return {
        acao: "ir_para",
        passo: bloco.aoResponder ?? passo.proximo ?? "",
        porque: "O contato respondeu, então o reengajamento foi cancelado.",
      };
    }

    const feitas = contexto.tentativas?.[passo.chave] ?? 0;
    const limite = Math.min(Math.max(1, bloco.tentativas), TETO_DE_TENTATIVAS);
    if (feitas >= limite) {
      return {
        acao: "ir_para",
        passo: bloco.aoDesistir,
        porque: `Foram ${feitas} tentativas sem resposta, então o fluxo desistiu.`,
      };
    }

    // A mensagem da vez. Menos mensagens que tentativas repete a última — quem escreveu uma só quis repetir.
    const texto = bloco.mensagens[Math.min(feitas, bloco.mensagens.length - 1)] ?? bloco.mensagens[0]!;
    return {
      acao: "reengajar",
      texto,
      tentativa: feitas + 1,
      minutos: Math.max(INTERVALO_MINIMO_MIN, bloco.intervaloMin),
    };
  }

  // `menu` cai fora daqui de propósito: quem o resolve é `proximoPasso`, que é reativa por mensagem.
  return {
    acao: "estourou",
    porque: `O passo '${passo.chave}' é um menu, e menu é resolvido pelo caminho reativo.`,
  };
}

function comparar(
  c: Comparacao,
  contexto: {
    readonly variaveis: Readonly<Record<string, string>>;
    readonly filaId: string | null;
    readonly canal: string | null;
    readonly etiquetas: readonly string[];
  },
): boolean {
  // Campos do atendimento vêm antes das variáveis: `fila` e `canal` são nomes que alguém vai usar como
  // variável sem querer, e o do atendimento é o que a pessoa quis dizer.
  const valor =
    c.variavel === "fila"
      ? (contexto.filaId ?? "")
      : c.variavel === "canal"
        ? (contexto.canal ?? "")
        : c.variavel === "etiqueta"
          ? contexto.etiquetas.join(",")
          : (contexto.variaveis[c.variavel] ?? "");

  const esperado = (c.valor ?? "").trim().toLowerCase();
  const atual = valor.trim().toLowerCase();

  if (c.operador === "existe") return atual !== "";
  if (c.operador === "nao_existe") return atual === "";
  if (c.operador === "igual") return atual === esperado;
  return esperado !== "" && atual.includes(esperado);
}

function descrever(c: Comparacao): string {
  if (c.operador === "existe") return `${c.variavel} tem valor`;
  if (c.operador === "nao_existe") return `${c.variavel} está vazio`;
  if (c.operador === "igual") return `${c.variavel} = '${c.valor}'`;
  return `${c.variavel} contém '${c.valor}'`;
}

/**
 * Os problemas dos blocos, somados aos do menu.
 *
 * Não substitui `problemasDoFluxo`: ela continua valendo para o menu, e este a estende. Um fluxo pode ter os
 * dois — um menu na entrada e blocos depois.
 */
export function problemasDosBlocos(passos: readonly PassoCompleto[]): readonly ProblemaDoFluxo[] {
  const problemas: ProblemaDoFluxo[] = [];
  const chaves = new Set(passos.map((p) => p.chave?.trim()).filter(Boolean));
  const coletadasAte = new Set<string>();

  for (const p of passos) {
    const tipo = p.tipo ?? "menu";
    const chave = p.chave?.trim() ?? "";

    if (tipo === "perguntar") {
      if (!p.guardaEm?.trim()) {
        problemas.push({
          bloqueante: true,
          passo: chave,
          texto:
            "Este passo pergunta e não diz onde guardar a resposta. Sem isso, o que o contato responder é " +
            "jogado fora, e a condição seguinte nunca casa.",
        });
      } else {
        coletadasAte.add(p.guardaEm.trim());
      }
    }

    if (tipo === "condicional") {
      if (!p.senao?.trim()) {
        problemas.push({
          bloqueante: true,
          passo: chave,
          texto:
            'Falta a saída para "nenhuma das opções". Sem ela o fluxo engole quem não casa nenhuma ' +
            "condição, e ninguém descobre até alguém reclamar.",
        });
      }
      if ((p.se ?? []).length === 0) {
        problemas.push({ bloqueante: true, passo: chave, texto: "Este passo condicional não tem condição." });
      }
      for (const c of p.se ?? []) {
        // Variável usada antes de coletada é o defeito que o §8d pede para pegar estaticamente — e ele é
        // invisível na leitura, porque o fluxo *parece* certo.
        const doAtendimento = ["fila", "canal", "etiqueta"].includes(c.variavel);
        if (!doAtendimento && !coletadasAte.has(c.variavel)) {
          problemas.push({
            bloqueante: false,
            passo: chave,
            texto:
              `A condição usa '${c.variavel}', que nenhum passo anterior coleta. Ela vai ser avaliada como ` +
              `vazia — o que pode estar certo, mas quase nunca é o que se quis.`,
          });
        }
      }
    }

    /*
      ## As duas ações que exigem MOTIVO, e por que são dois problemas e não um

      `finalizar` e `dar_como_perdida` fecham coisas diferentes — o ATENDIMENTO e o CARD —, com catálogos
      diferentes (`motivos` e `motivos_de_perda`) e telas diferentes para cadastrar cada um. Um problema
      genérico ("declare o motivo") faria quem lê procurar no catálogo errado. É a mesma razão de `aoFalhar`
      e `aoVazio` terem textos próprios, logo abaixo.

      ⚠️ **`finalizar` foi validada aqui em 2026-09-06, depois de MEDIR — e a assimetria anterior morreu com
      a medição.** Ela existia por uma premissa razoável: *"há fluxo publicado com `finalizar` sem motivo, e
      bloquear recusaria a próxima publicação de quem convive com ele"*. Medido na produção inteira (2 fluxos,
      1 tenant, 1 versão congelada, mais os rascunhos): **zero passos `finalizar` sem motivo, e zero passos
      `finalizar`** — e nenhum dos oito modelos de fábrica usa a ação, então a importação também não nasce
      recusada. Não havia ninguém para recusar.

      O que a guarda pega é o defeito MUDO de `executarAcao`: sem `motivoId` o passo dá `return` e a conversa
      **fica aberta**, sem erro, sem log e sem linha na trilha. Quem montou descobre no editor, e não no dia
      em que alguém pergunta por que a fila não esvazia.
    */
    if (tipo === "acao" && p.acao?.tipo === "finalizar" && !p.acao.motivoId?.trim()) {
      problemas.push({
        bloqueante: true,
        passo: chave,
        texto:
          "Este passo finaliza o atendimento e não diz o motivo de encerramento. Sem motivo ele não finaliza " +
          "nada: a conversa fica aberta, e nada avisa.",
      });
    }

    if (tipo === "acao" && p.acao?.tipo === "dar_como_perdida" && !p.acao.motivoDePerdaId?.trim()) {
      problemas.push({
        bloqueante: true,
        passo: chave,
        texto:
          "Este passo dá a oportunidade como perdida e não diz por quê. Sem motivo o card não fecha, e o " +
          "relatório de perdas não teria o que mostrar.",
      });
    }

    if (tipo === "agente") {
      const b = p.agente;
      if (!b?.objetivo?.trim()) {
        problemas.push({
          bloqueante: true,
          passo: chave,
          texto:
            "Este passo chama o assistente sem dizer o objetivo. Sem objetivo ele volta a ser o assistente " +
            "geral no meio de um caminho que precisa ser previsível.",
        });
      }
      if ((b?.saidasEsperadas ?? []).length === 0) {
        problemas.push({
          bloqueante: true,
          passo: chave,
          texto:
            "Este passo chama o assistente e não declara as saídas esperadas. Sem contrato de saída não há " +
            "como ligar o passo seguinte.",
        });
      }
      if (b && !b.aoEstourar && !p.proximo) {
        problemas.push({
          bloqueante: false,
          passo: chave,
          texto:
            "Não há para onde ir quando o assistente estoura o limite de turnos. A conversa vai para a fila " +
            "de desistência — o que funciona, mas é melhor escolher.",
        });
      }
    }

    if (tipo === "ferramenta") {
      const b = p.ferramenta;
      if (!b?.url?.trim()) {
        problemas.push({
          bloqueante: true,
          passo: chave,
          texto: "Este passo fala com um sistema de fora e não tem endereço.",
        });
      }
      // As DUAS saídas do §8c, e cada uma com o seu texto: "falha" e "vazio" são situações diferentes, e um
      // problema genérico ("declare as saídas") faria quem lê apontar as duas para o mesmo lugar.
      if (!b?.aoFalhar?.trim()) {
        problemas.push({
          bloqueante: true,
          passo: chave,
          texto:
            "Falta dizer para onde ir quando o sistema não responde. Sem isso, um ERP fora do ar deixa uma " +
            "fila de gente parada no meio do fluxo, e ninguém descobre até alguém reclamar.",
        });
      }
      if (!b?.aoVirVazio?.trim()) {
        problemas.push({
          bloqueante: true,
          passo: chave,
          texto:
            'Falta dizer para onde ir quando o sistema responde "não há nada". Tratar vazio como erro faz o ' +
            "contato receber uma mensagem de problema quando a resposta certa era uma informação.",
        });
      }
      if (b && (b.timeoutSeg <= 0 || b.timeoutSeg > TETO_DE_TIMEOUT_SEG)) {
        problemas.push({
          bloqueante: true,
          passo: chave,
          texto: `O tempo de espera é de ${b.timeoutSeg}s, e o limite é ${TETO_DE_TIMEOUT_SEG}s.`,
        });
      }
      if (b?.guardaEm?.trim()) coletadasAte.add(b.guardaEm.trim());
    }

    if (tipo === "reengajar") {
      const b = p.reengajar;
      if (!b || !(b.mensagens ?? []).some((m) => m.trim() !== "")) {
        problemas.push({
          bloqueante: true,
          passo: chave,
          texto: "Este passo reengaja e não tem mensagem para mandar.",
        });
      }
      if (!b?.aoDesistir?.trim()) {
        problemas.push({
          bloqueante: true,
          passo: chave,
          texto:
            "Falta dizer o que fazer quando as tentativas acabam. Desistir é obrigatório — reengajamento sem " +
            "fim é o produto perseguindo alguém.",
        });
      }
      if (b && b.tentativas > TETO_DE_TENTATIVAS) {
        problemas.push({
          bloqueante: true,
          passo: chave,
          texto: `São ${b.tentativas} tentativas, e o limite é ${TETO_DE_TENTATIVAS}.`,
        });
      }
      if (b && b.intervaloMin < INTERVALO_MINIMO_MIN) {
        problemas.push({
          bloqueante: true,
          passo: chave,
          // Três mensagens em dez minutos é o que faz o contato bloquear o número — e o número é o do cliente.
          texto: `O intervalo é de ${b.intervaloMin} minutos, e o mínimo é ${INTERVALO_MINIMO_MIN}.`,
        });
      }
    }

    if (tipo === "esperar") {
      const min = p.esperarMin ?? 0;
      if (min <= 0) {
        problemas.push({ bloqueante: true, passo: chave, texto: "Este passo espera, e não diz quanto tempo." });
      } else if (min > TETO_DE_ESPERA_MIN) {
        problemas.push({
          bloqueante: true,
          passo: chave,
          texto: `A espera é de ${min} minutos, e o limite é ${TETO_DE_ESPERA_MIN} (sete dias).`,
        });
      }
    }

    // Destinos dos blocos: o `menu` já é conferido por `problemasDoFluxo`.
    for (const [campo, destino] of [
      ["próximo", p.proximo],
      ["então", p.entao],
      ["senão", p.senao],
      ["ao estourar", p.agente?.aoEstourar],
      ["quando o sistema falha", p.ferramenta?.aoFalhar],
      ["quando o sistema volta vazio", p.ferramenta?.aoVirVazio],
      ["ao desistir", p.reengajar?.aoDesistir],
      ["quando o contato responde", p.reengajar?.aoResponder],
    ] as const) {
      if (destino && !chaves.has(destino)) {
        problemas.push({
          bloqueante: true,
          passo: chave,
          texto: `O destino de '${campo}' aponta para '${destino}', que não existe.`,
        });
      }
    }
  }

  return problemas;
}
